import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { CommerceLandingPort } from './domain/commerce-registry/ports/commerce-landing.port';
import { DocumentTypePort } from './domain/commerce-registry/ports/document-type.port';
import { CityPort } from './domain/commerce-registry/ports/city.port';
import { RegistrationValidationPort } from './domain/commerce-registry/ports/registration-validation.port';
import { ExistingUserProfilePort } from './domain/commerce-registry/ports/existing-user-profile.port';
import { AttributeValidationMeshPort } from './domain/commerce-registry/ports/attribute-validation-mesh.port';
import { commerceLandingPortFactory } from './infrastructure/commerce-registry/providers/commerce-landing-port.factory';
import { DocumentTypeApiAdapter } from './infrastructure/commerce-registry/adapters/document-type-api.adapter';
import { CityApiAdapter } from './infrastructure/commerce-registry/adapters/city-api.adapter';
import { RegistrationValidationApiAdapter } from './infrastructure/commerce-registry/adapters/registration-validation-api.adapter';
import { ExistingUserProfileApiAdapter } from './infrastructure/commerce-registry/adapters/existing-user-profile-api.adapter';
import { AttributeValidationMeshApiAdapter } from './infrastructure/commerce-registry/adapters/attribute-validation-mesh-api.adapter';
import { UserRegistrationPort } from './domain/commerce-registry/ports/user-registration.port';
import { UserRegistrationApiAdapter } from './infrastructure/commerce-registry/adapters/user-registration-api.adapter';
import { UpdateRegisteredUserPort } from './domain/commerce-registry/ports/update-registered-user.port';
import { UpdateRegisteredUserApiAdapter } from './infrastructure/commerce-registry/adapters/update-registered-user-api.adapter';
import { MsConsentPort } from './domain/commerce-registry/ports/ms-consent.port';
import { MsConsentApiAdapter } from './infrastructure/commerce-registry/adapters/ms-consent-api.adapter';
import { RegistryOtpPort } from './domain/commerce-registry/ports/registry-otp.port';
import { RegistryOtpApiAdapter } from './infrastructure/commerce-registry/adapters/registry-otp-api.adapter';
import { lealOtpAuthInterceptor } from './infrastructure/http/leal-otp-auth.interceptor';
import { LeaderboardPublicPort } from './domain/leaderboard/ports/leaderboard-public.port';
import { LeaderboardTriviaPort } from './domain/leaderboard/ports/leaderboard-trivia.port';
import { LeaderboardParticipantPort } from './domain/leaderboard/ports/leaderboard-participant.port';
import { leaderboardPublicPortFactory } from './infrastructure/leaderboard/providers/leaderboard-public-port.factory';
import { leaderboardTriviaPortFactory } from './infrastructure/leaderboard/providers/leaderboard-trivia-port.factory';
import { leaderboardParticipantPortFactory } from './infrastructure/leaderboard/providers/leaderboard-participant-port.factory';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    // No provideClientHydration: async landing + SSR often mismatch first paint vs client tree, which
    // can leave duplicate hydrated nodes (e.g. two hero banners). Client re-renders from bootstrap instead.
    provideHttpClient(withFetch(), withInterceptors([lealOtpAuthInterceptor])),
    {
      provide: CommerceLandingPort,
      useFactory: commerceLandingPortFactory,
      deps: [HttpClient],
    },
    {
      provide: DocumentTypePort,
      useClass: DocumentTypeApiAdapter,
    },
    {
      provide: CityPort,
      useClass: CityApiAdapter,
    },
    {
      provide: RegistrationValidationPort,
      useClass: RegistrationValidationApiAdapter,
    },
    {
      provide: ExistingUserProfilePort,
      useClass: ExistingUserProfileApiAdapter,
    },
    {
      provide: AttributeValidationMeshPort,
      useClass: AttributeValidationMeshApiAdapter,
    },
    {
      provide: UserRegistrationPort,
      useClass: UserRegistrationApiAdapter,
    },
    {
      provide: UpdateRegisteredUserPort,
      useClass: UpdateRegisteredUserApiAdapter,
    },
    {
      provide: MsConsentPort,
      useClass: MsConsentApiAdapter,
    },
    {
      provide: RegistryOtpPort,
      useClass: RegistryOtpApiAdapter,
    },
    {
      provide: LeaderboardPublicPort,
      useFactory: leaderboardPublicPortFactory,
      deps: [HttpClient],
    },
    {
      provide: LeaderboardTriviaPort,
      useFactory: leaderboardTriviaPortFactory,
      deps: [HttpClient],
    },
    {
      provide: LeaderboardParticipantPort,
      useFactory: leaderboardParticipantPortFactory,
      deps: [HttpClient],
    },
  ],
};
